import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Spin, Space } from 'antd';
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import './ImageViewer.less';

const ImageViewer = ({ visible, images = [], currentIndex = 0, onClose, onIndexChange }) => {
  const [currentIdx, setCurrentIdx] = useState(currentIndex);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCurrentIdx(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if (visible) {
      setScale(1);
    }
  }, [visible]);

  const currentImage = images[currentIdx];

  const handlePrev = () => {
    const newIndex = currentIdx > 0 ? currentIdx - 1 : images.length - 1;
    setCurrentIdx(newIndex);
    onIndexChange?.(newIndex);
    setScale(1);
  };

  const handleNext = () => {
    const newIndex = currentIdx < images.length - 1 ? currentIdx + 1 : 0;
    setCurrentIdx(newIndex);
    onIndexChange?.(newIndex);
    setScale(1);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleImageLoad = () => {
    setLoading(false);
  };

  // 监听当前图片变化，开始加载时显示loading
  useEffect(() => {
    if (currentImage) {
      setLoading(true);
    }
  }, [currentImage]);

  const handleKeyDown = e => {
    if (!visible) return;

    switch (e.key) {
      case 'ArrowLeft':
        handlePrev();
        break;
      case 'ArrowRight':
        handleNext();
        break;
      case 'Escape':
        onClose();
        break;
      case '+':
      case '=':
        handleZoomIn();
        break;
      case '-':
        handleZoomOut();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, currentIdx]);

  if (!visible || !currentImage) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width='90vw'
      style={{ top: 20 }}
      className='image-viewer-modal'
      destroyOnClose
      closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
    >
      <div className='image-viewer-container'>
        {/* 工具栏 */}
        <div className='image-viewer-toolbar'>
          <div className='image-viewer-info'>
            <span className='image-index'>
              {currentIdx + 1} / {images.length}
            </span>
            <span className='image-title'>{currentImage.title || `图片 ${currentIdx + 1}`}</span>
          </div>

          <div className='image-viewer-controls'>
            <Space>
              <Button
                icon={<LeftOutlined />}
                onClick={handlePrev}
                disabled={images.length <= 1}
                title='上一张 (←)'
              />
              <Button
                icon={<RightOutlined />}
                onClick={handleNext}
                disabled={images.length <= 1}
                title='下一张 (→)'
              />
              <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} title='缩小 (-)' />
              <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} title='放大 (+)' />
            </Space>
          </div>
        </div>

        {/* 图片显示区域 */}
        <div className='image-viewer-content'>
          <div className='image-wrapper'>
            {loading && (
              <div className='image-loading'>
                <Spin size='large' />
              </div>
            )}
            <img
              src={currentImage.url}
              alt={currentImage.title || `图片 ${currentIdx + 1}`}
              className='image-viewer-img'
              style={{
                transform: `scale(${scale})`,
                opacity: loading ? 0 : 1,
              }}
              onLoad={handleImageLoad}
              draggable={false}
            />
          </div>
        </div>

        {/* 缩略图导航 */}
        {images.length > 1 && (
          <div className='image-viewer-thumbnails'>
            {images.map((img, index) => (
              <div
                key={index}
                className={`thumbnail-item ${index === currentIdx ? 'active' : ''}`}
                onClick={() => {
                  setCurrentIdx(index);
                  onIndexChange?.(index);
                  setScale(1);
                }}
              >
                <img
                  src={img.url}
                  alt={img.title || `缩略图 ${index + 1}`}
                  className='thumbnail-img'
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

ImageViewer.propTypes = {
  visible: PropTypes.bool,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      title: PropTypes.string,
    })
  ),
  currentIndex: PropTypes.number,
  onClose: PropTypes.func,
  onIndexChange: PropTypes.func,
};

export default ImageViewer;
